import React, { useEffect, useState } from 'react';
import baseUrl from '../../config';

const DailyPerformanceDashboard = () => {
  const [currentDate, setCurrentDate] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [performanceData, setPerformanceData] = useState(null); // State to store fetched data
  const [loading, setLoading] = useState(true); // State for loading indicator
  const [error, setError] = useState(null); // State for error handling

  useEffect(() => {
    // Function to update current date and last updated time
    const updateDateTime = () => {
      const now = new Date();
      const optionsDate = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      const optionsTime = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true };

      setCurrentDate(now.toLocaleDateString('en-US', optionsDate));
      setLastUpdated(now.toLocaleTimeString('en-US', optionsTime));
    };

    // Function to fetch student performance data
    const fetchPerformanceData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Replace with your actual API endpoint for student performance
        const response = await fetch(baseUrl + 'api/students/performance', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`, // IMPORTANT: Replace with actual auth token
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setPerformanceData(data);
      } catch (err) {
        console.error("Error fetching performance data:", err);
        setError("Failed to load performance data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    // Initial updates
    updateDateTime();
    fetchPerformanceData();

    // Set up interval for date/time (optional, could be less frequent)
    const intervalId = setInterval(updateDateTime, 1000);

    // Cleanup function
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array means this runs once on mount and cleans up on unmount

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 font-sans p-6">
        <div className="flex flex-col items-center p-8 bg-white rounded-3xl shadow-xl">
          <div className="animate-bounce bg-blue-500 p-4 rounded-full mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
          </div>
          <p className="text-xl text-gray-700 font-semibold">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center font-inter bg-red-50">
        <div className="text-xl text-red-700">{error}</div>
      </div>
    );
  }

  // If data is null after loading and no error, it means no data was found or something went wrong
  if (!performanceData) {
    return (
      <div className="min-h-screen flex items-center justify-center font-inter bg-gray-100">
        <div className="text-xl text-gray-700">No performance data available.</div>
      </div>
    );
  }


  return (
    <div className="min-h-screen font-inter bg-gray-100 p-4"> {/* Added bg-gray-100 and p-4 for better spacing */}
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header Section */}
        <header className="bg-white rounded-3xl shadow-xl p-2 md:p-3 border-b-8 border-indigo-600 flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div className="flex items-center mb-4 sm:mb-0">
            <img src="https://placehold.co/80x80/6366F1/FFFFFF?text=S" alt="Student Avatar" className="rounded-full border-4 border-blue-300 shadow-md mr-4" />
            <div>
              <h2 className="text-3xl font-bold text-indigo-800">Hi, {performanceData.studentName || 'Student'}!</h2>
              <p className="text-lg text-blue-700">Today is <span id="currentDate" className="font-semibold">{currentDate}</span></p>
            </div>
          </div>
          <div>
            <div className="text-right">
              <p className="text-xl text-indigo-700 font-semibold">Overall Growth: <span className={`font-extrabold ${performanceData.overallGrowth === 'Excellent' ? 'text-green-600' : 'text-orange-600'}`}>{performanceData.overallGrowth || 'N/A'}</span></p>
              <p className="text-md text-blue-600">Last Updated: <span id="lastUpdated" className="font-medium">{lastUpdated}</span></p>
            </div>
          </div>
        </header>

        {/* Performance Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {performanceData.dailyPerformanceMetrics && performanceData.dailyPerformanceMetrics.map((metric, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-6 shadow-xl border border-${metric.color}-100 flex flex-col items-center text-center card-hover transition-all duration-300 fade-in`}
              style={{ animationDelay: `${0.2 + index * 0.1}s` }}
            >
              <div className={`bg-${metric.color}-100 rounded-full p-4 mb-4`}>
                <i className={`${metric.icon} text-${metric.color}-500 text-4xl`}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">{metric.status}</h3>
              <p className="text-gray-600 text-lg">{metric.description}</p>
            </div>
          ))}
        </div>

        {/* Progress & Activity Section */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 mb-12 fade-in" style={{ animationDelay: '0.6s' }}>
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
            <i className="fas fa-star text-teal-500 mr-3"></i>
            Today's Skill Development
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Skill Progress */}
            <div>
              <h3 className="text-2xl font-semibold text-gray-700 mb-4">Current Skill Progress</h3>
              {performanceData.skillProgress && performanceData.skillProgress.map((skill, index) => (
                <div key={index} className="mb-4">
                  <p className="text-lg text-gray-600 mb-2">
                    {skill.skill} <span className={`float-right font-semibold text-${skill.color}-600`}>{skill.progress}%</span>
                  </p>
                  <div className={`w-full bg-${skill.color}-100 rounded-full h-3`}>
                    <div className={`bg-gradient-to-r from-${skill.color}-400 to-${skill.color}-600 h-full rounded-full`} style={{ width: `${skill.progress}%` }}></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Activities (Enrichment Specific) */}
            <div>
              <h3 className="text-2xl font-semibold text-gray-700 mb-4">Recent Activities</h3>
              <ul className="space-y-3">
                {performanceData.recentActivities && performanceData.recentActivities.map((activity, index) => (
                  <li key={index} className="flex items-center bg-gray-50 p-3 rounded-lg shadow-sm border border-gray-100">
                    <i className={`${activity.icon} text-${activity.color}-500 mr-3 text-xl`}></i>
                    <span className="text-gray-700">{activity.description}</span>
                    <span className="ml-auto text-gray-500 text-sm">{activity.timeAgo}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* New Section: Past Class Performance */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 mb-12 fade-in" style={{ animationDelay: '0.8s' }}>
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
            <i className="fas fa-history text-orange-500 mr-3"></i>
            Past Class Performance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {performanceData.pastClassPerformance && performanceData.pastClassPerformance.map((pastClass, index) => (
              <div key={index} className="bg-gray-50 p-5 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-700 mb-2 flex items-center">
                  <i className="fas fa-calendar-alt text-blue-500 mr-2"></i>
                  {pastClass.className} ({pastClass.date})
                </h3>
                <ul className="list-disc list-inside text-gray-600 text-base space-y-1">
                  <li>Engagement: <span className={`font-semibold ${pastClass.engagement === 'High' || pastClass.engagement === 'Excellent' ? 'text-green-600' : 'text-yellow-600'}`}>{pastClass.engagement}</span></li>
                  <li>Participation: <span className={`font-semibold ${pastClass.participation === 'High' || pastClass.participation === 'Excellent' ? 'text-green-600' : 'text-yellow-600'}`}>{pastClass.participation}</span></li>
                  <li>Growth Area: {pastClass.growthArea}</li>
                  <li>Key Achievement: {pastClass.keyAchievement}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Motivational Quote */}
        <div className="text-center p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl shadow-lg border border-yellow-100 fade-in" style={{ animationDelay: '0.9s' }}>
          <p className="text-xl italic text-gray-700 mb-3">
            "Every day is a new chance to learn and grow!"
          </p>
          <p className="text-md font-semibold text-yellow-700">- Keep Shining!</p>
        </div>
      </div>

      <style>{`
        body {
            font-family: "Inter", sans-serif;
            background-color: #f8f8f8;
        }
        .card-hover:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
        }
        .fade-in {
            animation: fadeIn 0.8s ease-out forwards;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .animate-pop {
            animation: pop 0.3s ease-out forwards;
        }
        @keyframes pop {
            0% { transform: scale(0.8); opacity: 0; }
            80% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        .progress-bar-gradient {
            background: linear-gradient(to right, var(--tw-gradient-stops));
        }
      `}</style>
    </div>
  );
};

export default DailyPerformanceDashboard;